/**
 * React hooks for the Curated Integrations Admin API.
 *
 * Implemented with plain React useState/useEffect to stay consistent with the
 * existing `apiService` usage in the Administration Portal (no React Query
 * installed). The hook API surface is inspired by React Query's ergonomics:
 * queries expose `{ data, isLoading, error, refetch }` and mutations expose
 * `{ mutate, mutateAsync, isLoading, error, data }`.
 *
 * Auth is handled transparently by `apiService` (in-memory Bearer token +
 * automatic refresh via httpOnly cookie).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import type {
  CuratedIntegrationListItemDto,
  CreateCuratedIntegrationDto,
  UpdateCuratedIntegrationDto,
  UnpublishCuratedIntegrationResponse,
  StartDiscoveryResponse,
  DiscoveredMethodsResponseDto,
  IntegrationChangeDto,
  IntegrationConnectionItemDto,
  CuratedIntegrationNotificationsResponse,
  UpdateCuratedIntegrationNotificationsDto,
  ListCuratedIntegrationsFilter,
} from './curated-integrations.types';

// ─── Shared Query Result Types ────────────────────────────────────────────────

/** Shape returned by all query hooks. */
export interface QueryResult<TData> {
  /** Fetched data, undefined while loading or on error. */
  data: TData | undefined;
  /** True during the initial fetch and on refetch. */
  isLoading: boolean;
  /** Error thrown by the last fetch attempt, or null. */
  error: Error | null;
  /** Manually trigger a refetch. */
  refetch: () => void;
}

/** Shape returned by all mutation hooks. */
export interface MutationResult<TData, TVariables> {
  /**
   * Call the mutation.
   * Swallows errors — check `error` afterwards.
   */
  mutate: (variables: TVariables) => void;
  /**
   * Call the mutation and return a Promise.
   * Throws on error — use try/catch.
   */
  mutateAsync: (variables: TVariables) => Promise<TData>;
  /** True while the mutation request is in-flight. */
  isLoading: boolean;
  /** Last successful response data, or undefined. */
  data: TData | undefined;
  /** Error from the last mutation attempt, or null. */
  error: Error | null;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Generic query hook factory.
 * Re-fetches whenever `deps` changes (like useEffect deps).
 * Uses a ref-based "refetch counter" so callers can trigger manual refetches.
 */
function useQuery<TData>(
  fetcher: () => Promise<TData>,
  deps: unknown[],
): QueryResult<TData> {
  const [data, setData] = useState<TData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchIndex, setRefetchIndex] = useState(0);

  const refetch = useCallback(() => setRefetchIndex((i) => i + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refetchIndex]);

  return { data, isLoading, error, refetch };
}

/**
 * Generic mutation hook factory.
 * Returns a stable `mutate` + `mutateAsync` pair.
 */
function useMutation<TData, TVariables>(
  mutator: (variables: TVariables) => Promise<TData>,
): MutationResult<TData, TVariables> {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TData | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);

  // Stable ref so consumers can safely call mutateAsync from event handlers
  const mutatorRef = useRef(mutator);
  mutatorRef.current = mutator;

  const mutateAsync = useCallback(async (variables: TVariables): Promise<TData> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await mutatorRef.current(variables);
      setData(result);
      return result;
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      setError(wrapped);
      throw wrapped;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const mutate = useCallback(
    (variables: TVariables) => {
      mutateAsync(variables).catch(() => {
        // Error is stored in `error` state; caller can check it
      });
    },
    [mutateAsync],
  );

  return { mutate, mutateAsync, isLoading, data, error };
}

// ─── Query Hooks ──────────────────────────────────────────────────────────────

/**
 * Fetches the list of all curated integrations.
 * Endpoint: GET /api/admin/curated-integrations
 *
 * @param filter Optional filter by publication status ('draft' | 'published')
 */
export function useListCuratedIntegrations(
  filter?: ListCuratedIntegrationsFilter,
): QueryResult<CuratedIntegrationListItemDto[]> {
  return useQuery(
    () => apiService.listCuratedIntegrations(filter),
    [filter?.status],
  );
}

/**
 * Fetches a single curated integration by ID.
 * Endpoint: GET /api/admin/curated-integrations/:id
 *
 * @param id UUID of the integration
 */
export function useGetCuratedIntegration(
  id: string,
): QueryResult<CuratedIntegrationListItemDto> {
  return useQuery(
    () => apiService.getCuratedIntegration(id),
    [id],
  );
}

/**
 * Fetches all discovered methods for a curated integration.
 * Requires the admin's MCP connection to be in 'connected' status.
 * Endpoint: GET /api/admin/curated-integrations/:id/discovered-methods
 *
 * @param id UUID of the integration
 */
export function useGetDiscoveredMethods(
  id: string,
): QueryResult<DiscoveredMethodsResponseDto> {
  return useQuery(
    () => apiService.getCuratedIntegrationDiscoveredMethods(id),
    [id],
  );
}

/**
 * Fetches the method change log for a curated integration.
 * Endpoint: GET /api/admin/curated-integrations/:id/changes
 *
 * @param id UUID of the integration
 */
export function useListIntegrationChanges(
  id: string,
): QueryResult<IntegrationChangeDto[]> {
  return useQuery(
    () => apiService.listCuratedIntegrationChanges(id),
    [id],
  );
}

/**
 * Fetches all active customer agent connections for a curated integration.
 * Endpoint: GET /api/admin/curated-integrations/:id/connections
 *
 * @param id UUID of the integration
 */
export function useListIntegrationConnections(
  id: string,
): QueryResult<IntegrationConnectionItemDto[]> {
  return useQuery(
    () => apiService.listCuratedIntegrationConnections(id),
    [id],
  );
}

/**
 * Fetches the current list of notification recipients for curated integration changes.
 * Endpoint: GET /api/admin/system-settings/curated-integration-notifications
 */
export function useGetCuratedNotificationRecipients(): QueryResult<CuratedIntegrationNotificationsResponse> {
  return useQuery(
    () => apiService.getCuratedIntegrationNotificationRecipients(),
    [],
  );
}

// ─── Mutation Hooks ───────────────────────────────────────────────────────────

/**
 * Creates a new curated integration draft.
 * Endpoint: POST /api/admin/curated-integrations
 */
export function useCreateCuratedIntegration(): MutationResult<
  CuratedIntegrationListItemDto,
  CreateCuratedIntegrationDto
> {
  return useMutation((dto) => apiService.createCuratedIntegration(dto));
}

/**
 * Updates an existing curated integration.
 * Endpoint: PUT /api/admin/curated-integrations/:id
 */
export function useUpdateCuratedIntegration(): MutationResult<
  CuratedIntegrationListItemDto,
  { id: string; dto: UpdateCuratedIntegrationDto }
> {
  return useMutation(({ id, dto }) => apiService.updateCuratedIntegration(id, dto));
}

/**
 * Deletes a curated integration.
 * Only possible when connectionCount === 0 (backend returns 409 otherwise).
 * Endpoint: DELETE /api/admin/curated-integrations/:id
 */
export function useDeleteCuratedIntegration(): MutationResult<void, string> {
  return useMutation((id) => apiService.deleteCuratedIntegration(id));
}

/**
 * Uploads an icon image for a curated integration (multipart/form-data).
 * Endpoint: POST /api/admin/curated-integrations/:id/icon
 *
 * Returns the updated integration including the new iconUrl.
 */
export function useUploadCuratedIcon(): MutationResult<
  CuratedIntegrationListItemDto,
  { id: string; file: File }
> {
  return useMutation(({ id, file }) => apiService.uploadCuratedIntegrationIcon(id, file));
}

/**
 * Starts the OAuth discovery flow for the admin connection of a curated integration.
 * Response contains `authorizationUrl` which must be opened in a new window/tab
 * so the admin can authenticate with the upstream MCP provider.
 * Endpoint: POST /api/admin/curated-integrations/:id/discovery/start
 */
export function useStartDiscovery(): MutationResult<StartDiscoveryResponse, string> {
  return useMutation((id) => apiService.startCuratedIntegrationDiscovery(id));
}

/**
 * Publishes a curated integration (Draft → Published).
 * Requires at least one enabled method.
 * Endpoint: POST /api/admin/curated-integrations/:id/publish
 */
export function usePublishCuratedIntegration(): MutationResult<
  CuratedIntegrationListItemDto,
  string
> {
  return useMutation((id) => apiService.publishCuratedIntegration(id));
}

/**
 * Unpublishes a curated integration (Published → Draft).
 * F2: The response includes `affectedConnectionCount` — the number of existing
 * customer connections that remain active during the grace period.
 * Use this count in the confirmation dialog to warn the admin.
 * Endpoint: POST /api/admin/curated-integrations/:id/unpublish
 */
export function useUnpublishCuratedIntegration(): MutationResult<
  UnpublishCuratedIntegrationResponse,
  string
> {
  return useMutation((id) => apiService.unpublishCuratedIntegration(id));
}

/**
 * Triggers a re-sync of discovered methods for a curated integration.
 * Subject to rate limiting (1 resync/min per integration — backend returns 429).
 * Endpoint: POST /api/admin/curated-integrations/:id/resync
 */
export function useTriggerResync(): MutationResult<void, string> {
  return useMutation((id) => apiService.triggerCuratedIntegrationResync(id));
}

/**
 * Acknowledges a single method change log entry.
 * F2/Optimistic: callers may optimistically hide the row before the request settles.
 * Endpoint: POST /api/admin/curated-integrations/:id/changes/:logId/acknowledge
 */
export function useAcknowledgeChange(): MutationResult<
  void,
  { integrationId: string; logId: string }
> {
  return useMutation(({ integrationId, logId }) =>
    apiService.acknowledgeCuratedIntegrationChange(integrationId, logId),
  );
}

/**
 * Updates the email notification recipients list for curated integration changes.
 * Validates email addresses; backend enforces max 50 recipients.
 * Endpoint: PUT /api/admin/system-settings/curated-integration-notifications
 */
export function useUpdateCuratedNotificationRecipients(): MutationResult<
  CuratedIntegrationNotificationsResponse,
  UpdateCuratedIntegrationNotificationsDto
> {
  return useMutation((dto) =>
    apiService.updateCuratedIntegrationNotificationRecipients(dto),
  );
}
